import { supabase } from './supabase'

// Sorts segments by segment_number ascending.
export function sortSegments(segs) {
  return [...(segs || [])].sort(function (a, b) { return (a.segment_number || 0) - (b.segment_number || 0) })
}

// Returns { startError, overlapErrors: [{ idx, msg }] }.
// A segment is invalid if end_date < start_date, or if it starts on/before the prior segment's end_date.
export function validateSegments(segs) {
  var sorted = sortSegments(segs)
  var errors = []
  var first = sorted[0]
  var startError = null
  if (!first || !first.start_date) startError = 'At least one segment with a start date is required.'
  for (var i = 0; i < sorted.length; i++) {
    var s = sorted[i]
    if (s.start_date && s.end_date && s.end_date < s.start_date) {
      errors.push({ idx: i, msg: 'End date is before start date.' })
    }
    if (i > 0) {
      var prev = sorted[i - 1]
      if (prev.end_date && s.start_date && s.start_date <= prev.end_date) {
        errors.push({ idx: i, msg: 'Starts on or before Visit ' + (prev.segment_number || i) + ' ends.' })
      }
    }
  }
  return { startError: startError, overlapErrors: errors }
}

// From a segment list, return { start_date, end_date } for the overall job (min/max).
export function segmentRange(segs) {
  var starts = (segs || []).map(function (s) { return s.start_date }).filter(Boolean).sort()
  var ends = (segs || []).map(function (s) { return s.end_date || s.start_date }).filter(Boolean).sort()
  return { start_date: starts[0] || null, end_date: ends[ends.length - 1] || null }
}

// Given a list of segments and a date (YYYY-MM-DD), pick the active one:
// - if any segment contains the date → that one
// - else the next future segment
// - else the most recent past one
export function pickRelevantSegment(segs, todayIso) {
  var sorted = sortSegments(segs).filter(function (s) { return s.start_date })
  if (sorted.length === 0) return null
  for (var i = 0; i < sorted.length; i++) {
    var s = sorted[i]
    if ((s.start_date || '') <= todayIso && todayIso <= (s.end_date || s.start_date)) return s
  }
  var future = sorted.find(function (s) { return s.start_date > todayIso })
  if (future) return future
  return sorted[sorted.length - 1]
}

// Replace all segments for a job in one transaction-ish pass: delete then insert.
// Returns { data, error }.
export async function writeSegments(jobId, segs) {
  var cleaned = sortSegments(segs).filter(function (s) { return s.start_date })
  if (cleaned.length === 0) {
    var del = await supabase.from('job_segments').delete().eq('job_id', jobId)
    if (del.error) return { data: null, error: del.error }
    await supabase.from('jobs').update({ has_segments: false }).eq('id', jobId)
    return { data: [], error: null }
  }
  var delErr = (await supabase.from('job_segments').delete().eq('job_id', jobId)).error
  if (delErr) return { data: null, error: delErr }
  var rows = cleaned.map(function (s, i) {
    return {
      job_id: jobId,
      segment_number: i + 1,
      start_date: s.start_date,
      end_date: s.end_date || s.start_date,
      description: s.description || null,
      crew_size_needed: s.crew_size_needed ? parseInt(s.crew_size_needed, 10) : null,
      status: s.status || 'scheduled',
      notes: s.notes || null,
    }
  })
  var ins = await supabase.from('job_segments').insert(rows).select()
  if (ins.error) return { data: null, error: ins.error }
  var range = segmentRange(rows)
  await supabase.from('jobs').update({
    has_segments: true,
    start_date: range.start_date,
    end_date: range.end_date,
  }).eq('id', jobId)
  return { data: ins.data, error: null }
}

// Counts days inclusive in a single segment (end - start + 1).
export function segmentDays(s) {
  if (!s || !s.start_date) return 0
  var start = new Date(s.start_date + 'T12:00:00')
  var end = new Date((s.end_date || s.start_date) + 'T12:00:00')
  return Math.round((end - start) / 86400000) + 1
}

// Biggest gap in days between consecutive visits, and the total inter-visit days.
// Returns { maxGapDays, totalGapDays, firstStart, lastEnd }.
export function segmentGaps(segs) {
  var sorted = sortSegments(segs).filter(function (s) { return s.start_date })
  if (sorted.length < 2) return { maxGapDays: 0, totalGapDays: 0, firstStart: sorted[0]?.start_date || null, lastEnd: sorted[0]?.end_date || sorted[0]?.start_date || null }
  var max = 0
  var total = 0
  for (var i = 1; i < sorted.length; i++) {
    var prevEnd = new Date((sorted[i - 1].end_date || sorted[i - 1].start_date) + 'T12:00:00')
    var curStart = new Date(sorted[i].start_date + 'T12:00:00')
    var gap = Math.round((curStart - prevEnd) / 86400000) - 1
    if (gap < 0) gap = 0
    if (gap > max) max = gap
    total += gap
  }
  return {
    maxGapDays: max,
    totalGapDays: total,
    firstStart: sorted[0].start_date,
    lastEnd: sorted[sorted.length - 1].end_date || sorted[sorted.length - 1].start_date,
  }
}
